import type React from 'react';
import type { HocuspocusProvider } from '@hocuspocus/provider';
import type { AiSuggestion, Artifact } from '@/lib/api';
import type { AiActivityState, ArtifactAiClientError } from '@/hooks/useArtifactAi';
import { ArtifactAiActivityOverlay } from './ArtifactAiActivityOverlay';
import { ArtifactDraftPreview } from './ArtifactDraftPreview';
import { CollaborativeBodyEditor } from './CollaborativeBodyEditor';

interface ArtifactBodyPanelProps {
  artifact: Artifact;
  onProviderChange?: (provider: HocuspocusProvider | null) => void;
  /** When non-null, render the AI draft preview overlaying the collab editor. */
  draftSuggestion?: AiSuggestion | null;
  accepting?: boolean;
  rejecting?: boolean;
  aiDisabled?: boolean;
  onAcceptDraft?: () => void;
  onRejectDraft?: () => void;
  onCloseDraft?: () => void;
  /** Live AI generate/suggest activity. When provided and mode is generate/suggest, overlay is shown. */
  aiActivityMode?: 'generate' | 'suggest' | null;
  aiActivity?: AiActivityState;
  aiActivityError?: ArtifactAiClientError | null;
  aiActivityProvider?: string | null;
  aiActivityModel?: string | null;
  onCancelAiActivity?: () => void;
}

export function ArtifactBodyPanel({
  artifact,
  onProviderChange,
  draftSuggestion = null,
  accepting = false,
  rejecting = false,
  aiDisabled = false,
  onAcceptDraft,
  onRejectDraft,
  onCloseDraft,
  aiActivityMode = null,
  aiActivity,
  aiActivityError = null,
  aiActivityProvider = null,
  aiActivityModel = null,
  onCancelAiActivity,
}: ArtifactBodyPanelProps): React.ReactElement {
  const showActivity = aiActivityMode !== null && aiActivity !== undefined;
  const showDraft = !showActivity && draftSuggestion !== null;
  const hideEditor = showActivity || showDraft;
  return (
    <div className="flex flex-col gap-2">
      {showActivity && aiActivity !== undefined && (
        <ArtifactAiActivityOverlay
          mode={aiActivityMode}
          providerId={aiActivityProvider}
          model={aiActivityModel}
          activity={aiActivity}
          error={aiActivityError}
          onCancel={() => onCancelAiActivity?.()}
        />
      )}
      {showDraft && draftSuggestion !== null && (
        <ArtifactDraftPreview
          suggestion={draftSuggestion}
          accepting={accepting}
          rejecting={rejecting}
          disabled={aiDisabled}
          onAccept={() => onAcceptDraft?.()}
          onReject={() => onRejectDraft?.()}
          onBack={() => onCloseDraft?.()}
        />
      )}
      {/* Keep the collab editor mounted to preserve the websocket connection
          while previewing a draft or showing live AI activity; hide it visually + from a11y tree. */}
      <div aria-hidden={hideEditor} className={hideEditor ? 'hidden' : undefined}>
        <CollaborativeBodyEditor artifact={artifact} onProviderChange={onProviderChange} />
      </div>
    </div>
  );
}
